import React, {useEffect, useState} from 'react';

import omit from 'lodash/omit';
import pick from 'lodash/pick';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {CheckboxField} from 'app/general/widgets/CheckboxField';
import StateButton from 'app/general/widgets/StateButton';
import {
  useCreateSignoffTemplate,
  useGetSignoffTemplate,
  useUpdateSignoffTemplate,
} from 'app/signoffs/api';
import {TemplateOptionsType} from 'app/signoffs/types';
import {gt} from 'app/utils/i18n';

import type {TeamSerializedType} from 'sweet-types';

interface Props {
  show: boolean;
  toggle: () => void;
  team: TeamSerializedType;
  className?: string;
}

export function ManageSignoffTemplateModal({
  show,
  toggle,
  team,
}: Props): JSX.Element {
  const [templateOptions, setTemplateOptions] = useState<TemplateOptionsType>({
    is_trigger_on_create: true,
    is_trigger_new_document: true,
    is_trigger_new_member: true,
    is_trigger_on_approval: false,
    is_trigger_on_signoff_requested: true,
    is_trigger_on_draft_approval: true,
  });

  const updateOption = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, checked} = e.target;
    setTemplateOptions((options) => ({
      ...options,
      [name]: checked,
    }));
  };

  const [createSignoffTemplate, {isSuccess: isCreateSuccess}] =
    useCreateSignoffTemplate();

  const [updateSignoffTemplate, {isSuccess: isUpdateSuccess}] =
    useUpdateSignoffTemplate();

  const {data: signoffTemplate, isLoading} = useGetSignoffTemplate(
    {url: team.signofftemplate || ''},
    {skip: !show || team.signofftemplate == null}
  );

  function createOrUpdate() {
    return team.signofftemplate != null
      ? updateSignoffTemplate({
          url: team.signofftemplate,
          rules: {...omit(templateOptions, ['is_trigger_on_create'])},
        }).unwrap()
      : createSignoffTemplate({
          team,
          rules: {...templateOptions},
        }).unwrap();
  }

  useEffect(() => {
    if (isCreateSuccess || isUpdateSuccess) {
      toggle();
    }
  }, [isCreateSuccess, isUpdateSuccess]);

  useEffect(() => {
    // Update template options when signoff template data is loaded
    if (signoffTemplate) {
      setTemplateOptions(
        pick(signoffTemplate, [
          'is_trigger_new_document',
          'is_trigger_new_member',
          'is_trigger_on_approval',
          'is_trigger_on_signoff_requested',
          'is_trigger_on_create',
          'is_trigger_on_draft_approval',
        ])
      );
    }
  }, [signoffTemplate]);

  return (
    <TabbedModal
      show={show}
      onCancel={toggle}
      tabs={[
        {
          eventKey: 'add-signoff',
          name:
            team.signofftemplate != null
              ? gt('Edit Sign Off Rules')
              : gt('Add Sign Off Rules'),
          icon: 'fa fa-signature',
          content: (
            <>
              <div className="form-group">
                <p>
                  {isLoading ? (
                    <span>
                      {gt('Loading...')}
                      <i className="fa fa-spinner fa-spin ml-1" />
                    </span>
                  ) : (
                    gt('Sign off required for:')
                  )}
                </p>
                {team.signofftemplate == null ? (
                  <CheckboxField
                    id="id_is_trigger_on_create"
                    name="is_trigger_on_create"
                    disabled={isLoading}
                    value={templateOptions['is_trigger_on_create']}
                    onChange={updateOption}
                    label={gt(
                      'Existing approved procedures, policies and processes.'
                    )}
                    labelClass="col-10"
                  />
                ) : null}
                <CheckboxField
                  id="id_is_trigger_new_document"
                  name="is_trigger_new_document"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_new_document']}
                  onChange={updateOption}
                  label={gt('New approved documents when added to this team.')}
                  labelClass="col-10"
                  helpText={gt(
                    'Sign off not sent for draft documents, only approved documents at the time they are added.'
                  )}
                />
                <CheckboxField
                  id="id_is_trigger_new_member"
                  name="is_trigger_new_member"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_new_member']}
                  onChange={updateOption}
                  label={gt('New members of this team.')}
                  labelClass="col-10"
                />
                <CheckboxField
                  id="id_is_trigger_on_approval"
                  name="is_trigger_on_approval"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_on_approval']}
                  onChange={updateOption}
                  label={gt('Each new document approval.')}
                  labelClass="col-10"
                />
                <CheckboxField
                  id="is_trigger_on_draft_approval"
                  name="is_trigger_on_draft_approval"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_on_draft_approval']}
                  onChange={updateOption}
                  label={gt('Draft documents that are approved.')}
                  labelClass="col-10"
                  helpText={gt(
                    'Sent when a draft document is approved for the first time.'
                  )}
                />
                <CheckboxField
                  id="is_trigger_on_signoff_requested"
                  name="is_trigger_on_signoff_requested"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_on_signoff_requested']}
                  onChange={updateOption}
                  label={gt('Managers requesting sign off.')}
                  labelClass="col-10"
                  helpText={gt(
                    'Manually request sign off for individual documents.'
                  )}
                />
              </div>
              <StyledModalFooter className="mt-3">
                <button
                  onClick={toggle}
                  className="btn btn-link text-secondary"
                >
                  <i className="fa fa-times mr-1" />
                  {gt('Close')}
                </button>
                <StateButton
                  onClick={createOrUpdate}
                  busyText={
                    team.signofftemplate != null
                      ? gt('Updating')
                      : gt('Creating')
                  }
                  className="btn-primary"
                >
                  {team.signofftemplate != null ? gt('Save') : gt('Create')}
                </StateButton>
              </StyledModalFooter>
            </>
          ),
        },
      ]}
    />
  );
}
